import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import {
    FormBuilder,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { MeetingNoteDto } from 'app/api/models';
import { ProjectService } from 'app/api/services';
import { CommonModule, DatePipe } from '@angular/common';
import { SharedModule } from 'app/core/shared.module';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { DomSanitizer } from '@angular/platform-browser';

@Component({
    selector: 'app-public-meeting-note',
    template: `
        <div class="flex flex-col w-[100vw] h-[100vh] py-12">
            @if (!isCorrectPassword) {
                <form
                    [formGroup]="noteForm"
                    class="flex flex-col gap-8 items-center my-auto mx-[20vw] min-w-[400px]"
                >
                    <div class="text-xl">
                        Please enter password to view the Meeting Note.
                    </div>
                    <mat-form-field
                        class="w-full fuse-mat-dense"
                        [subscriptSizing]="'dynamic'"
                    >
                        <mat-label>Password</mat-label>
                        <mat-icon
                            class="icon-size-5"
                            [svgIcon]="'heroicons_solid:lock-closed'"
                            matSuffix
                        ></mat-icon>
                        <input
                            name="password"
                            type="password"
                            [formControlName]="'password'"
                            matInput
                        />
                    </mat-form-field>
                    <button
                        mat-flat-button
                        type="submit"
                        [color]="'primary'"
                        (click)="tryLogin()"
                        [disabled]="!noteForm.valid || noteForm.pristine"
                    >
                        View
                    </button>
                </form>
            }

            @if (isCorrectPassword && meetingNote) {
                <div
                    class="w-full max-w-4xl min-w-[70vw] mx-auto p-8 bg-card shadow rounded-lg"
                >
                    <div class="flex gap-4 ml-2 pt-4 pb-8 items-center">
                        <div class="flex-0 h-10 overflow-hidden">
                            <img
                                class="w-full h-full"
                                src="assets/images/logo/HC_red.png"
                            />
                        </div>
                        <div class="text-xl">Meeting Note</div>
                    </div>
                    <div class="meeting-note-container">
                        <div class="text-2xl font-semibold mb-4">
                            {{ meetingNote.title }}
                        </div>
                        <div class="text-secondary mb-6">
                            {{ meetingNote.date | date: 'd MMM yyyy' }}
                            {{ meetingNote.time }}
                        </div>
                        <div
                            class="quill-content border-t pt-4"
                            [innerHTML]="getSafeHtml(meetingNote.notes)"
                        ></div>
                    </div>
                </div>
            }
        </div>
    `,
    styles: [
        `
            .quill-content {
                ::ng-deep {
                    h1,
                    h2,
                    h3,
                    h4,
                    h5,
                    h6 {
                        margin-top: 0.5em;
                        margin-bottom: 0.3em;
                    }

                    h1 {
                        font-size: 1.5em;
                    }

                    h2 {
                        font-size: 1.3em;
                    }

                    ul,
                    ol {
                        margin-left: 1em;
                        margin-bottom: 0.5em;
                    }

                    ul {
                        list-style-type: disc;
                    }

                    ol {
                        list-style-type: decimal;
                    }

                    a {
                        color: #1976d2;
                        text-decoration: underline;
                    }

                    blockquote {
                        border-left: 3px solid #ccc;
                        margin-left: 0.5em;
                        padding-left: 0.5em;
                    }

                    code {
                        background-color: #f0f0f0;
                        padding: 0.2em 0.4em;
                        border-radius: 3px;
                        font-family: monospace;
                    }
                }
            }

            .mat-mdc-unelevated-button:not(:disabled) {
                background-color: var(--fuse-primary);
                color: white;
            }
        `,
    ],
    imports: [
        CommonModule,
        SharedModule,
        MatButtonModule,
        MatIconModule,
        MatInputModule,
        MatFormFieldModule,
        FormsModule,
        ReactiveFormsModule,
    ],
})
export class PublicMeetingNoteComponent implements OnInit {
    public noteForm: FormGroup;
    public isCorrectPassword: boolean = false;
    public meetingNote: MeetingNoteDto;
    public noteId: string;

    constructor(
        private projectService: ProjectService,
        private cdr: ChangeDetectorRef,
        private route: ActivatedRoute,
        private _formBuilder: FormBuilder,
        private sanitizer: DomSanitizer,
    ) {}

    ngOnInit(): void {
        this.buildForm();

        this.route.params.subscribe((res) => {
            this.noteId = res['id'];

            if (!this.noteId) return;

            this.loadPublicMeetingNote(this.noteId);
        });
    }

    buildForm() {
        this.noteForm = this._formBuilder.group({
            password: ['', Validators.required],
        });

        this.cdr.detectChanges();
    }

    loadPublicMeetingNote(noteId: string) {
        this.projectService
            .apiProjectPublicMeetingNoteGet$Json$Response({ noteId: noteId })
            .subscribe((res) => {
                if (!res.body.data) return;

                this.meetingNote = res.body.data;
                this.cdr.detectChanges();
            });
    }

    tryLogin() {
        const password = this.noteForm.value.password;
        this.projectService
            .apiProjectCheckMeetingNotePasswordGet$Json$Response({
                noteId: this.noteId,
                password: password,
            })
            .subscribe((res) => {
                if (!res?.body?.data) return;

                this.isCorrectPassword = res.body.data;
                this.cdr.detectChanges();
            });
    }

    getSafeHtml(html: string) {
        return this.sanitizer.bypassSecurityTrustHtml(html);
    }
}
